<script setup lang="ts">
import { ref } from 'vue';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { CalendarDaysIcon, CreditCardIcon, UserCircleIcon, ChevronUpIcon } from '@heroicons/vue/20/solid';
import { TicketIcon } from '@heroicons/vue/24/outline'
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue';


/** @TODO Retrieve from API */
export interface Ticket {
  airlineIata: string;
  ticketNumber: string;
  ticketStatus: string;
  currency: string | null;
  price: string | null;
  issueDate: Date;
  officeId: string;
  issuingAgencyNumber: string;
  flights: TicketFlight[];
  passengerLastName: string;
  passengerFirstName: string;
}

export interface TicketFlight {
  flightIdentifier: string;
  airlineIata: string;
  flightNumber: string;
  pricingClass: string;
  date: string;
  departureAirport: string;
  arrivalAirport: string;
  rawDepartureDate: string;
}

const props = defineProps<{ ticket: Ticket }>();
const ticket = ref(props.ticket)
</script>
<template>
  <div v-if="ticket?.airlineIata" class="lg:col-start-3 lg:row-end-1">
    <h2 class="sr-only">Ticket</h2>
    <div class="rounded-lg shadow-sm bg-gray-50 ring-1 ring-gray-900/5">
      <dl class="flex flex-wrap">
        <div class="flex-auto pt-6 pl-6 text-left">
          <dt class="text-sm font-semibold leading-6 text-gray-900">Ticket</dt>
          <dd class="mt-1 text-base font-semibold leading-6 text-gray-900">{{ ticket.ticketNumber }}</dd>
        </div>
        <div class="self-end flex-none px-6 pt-4">
          <dt class="sr-only">Status</dt>
          <dd class="inline-flex items-center px-2 py-1 text-xs font-medium text-green-700 rounded-md bg-green-50 ring-1 ring-inset ring-green-600/20">
            E-ticket
          </dd>
        </div>
        <div class="flex flex-none w-full px-6 pt-6 mt-6 border-t gap-x-4 border-gray-900/5">
          <dt class="flex-none">
            <span class="sr-only">Passager</span>
            <UserCircleIcon class="w-5 h-6 text-gray-400" aria-hidden="true" />
          </dt>
          <dd class="text-sm font-medium leading-6 text-gray-900">{{ ticket.passengerFirstName }} {{ ticket.passengerLastName.toUpperCase() }}</dd>
        </div>
        <!-- <div class="flex flex-none w-full px-6 mt-4 gap-x-4">
          <dt class="flex-none">
            <span class="sr-only">Ticket</span>
            <TicketIcon class="w-5 h-6 text-gray-400" aria-hidden="true" />
          </dt>
          <dd class="text-sm leading-6 text-gray-900">Billet : {{ ticket.ticketNumber }}</dd>
        </div> -->
        <div class="flex flex-none w-full px-6 mt-4 gap-x-4">
          <dt class="flex-none">
            <span class="sr-only">Due date</span>
            <CalendarDaysIcon class="w-5 h-6 text-gray-400" aria-hidden="true" />
          </dt>
          <dd class="text-sm leading-6 text-gray-500">
            <time datetime="2023-01-31">Émis le {{ format(new Date(ticket.issueDate), 'd MMMM yyyy', { locale: fr }) }}</time>
          </dd>
        </div>
        <div class="flex flex-none w-full px-6 mt-4 gap-x-4">
          <dt class="flex-none">
            <span class="sr-only">Status</span>
            <CreditCardIcon class="w-5 h-6 text-gray-400" aria-hidden="true" />
          </dt>
          <dd class="text-sm leading-6 text-gray-500">{{ ticket.price || '(inconnu)' }} {{ ticket.currency || '' }}</dd>
        </div>
      </dl>
      <div class="px-6 py-6 mt-6 border-t border-gray-900/5">
        <p class="text-sm font-semibold leading-6 text-gray-900">
          Itinéraire
          <Disclosure as="div" class="mb-2" v-for="(flight, index) in ticket.flights" v-slot="{ open }">
            <DisclosureButton
              class="flex justify-between w-full px-4 py-2 text-sm font-medium text-left text-purple-900 bg-purple-100 rounded-lg hover:bg-purple-200 focus:outline-none focus-visible:ring focus-visible:ring-purple-500 focus-visible:ring-opacity-75"
            >
              <span class="text-gray-600">({{ index + 1 }})</span>
              <span class="font-medium text-gray-800">{{ flight.flightIdentifier }}</span>
              <p>
                {{ flight.departureAirport }} <span aria-hidden="true">&rarr;</span> {{ flight.arrivalAirport }}
              </p>
              <span class="text-gray-600">{{ flight.rawDepartureDate }}</span>
              <ChevronUpIcon
                :class="open ? 'rotate-180 transform' : ''"
                class="w-5 h-5 text-purple-500"
              />
            </DisclosureButton>
            <DisclosurePanel class="px-4 pt-4 pb-2 text-sm text-gray-500">
              Information sur le vol à venir...
            </DisclosurePanel>
          </Disclosure>
        </p>
      </div>
    </div>
  </div>
</template>